// ViewModal.jsx

import React, { useCallback, useState } from 'react';

// Context
import { useProgress } from 'context/ProgressContext';
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Services
import { deleteViewByKey } from 'services/views/ViewWriteService';

// Hooks
import useViewDefinitionEditor from './hooks/useViewDefinitionEditor';
import useViewModalSave from './hooks/useViewModalSave';

// CSS
import './ViewModal.css';

// Components
import { buildViewDefinitionDraft } from './buildViewDefinitionDraft';
import ViewModalFooter from './footer/ViewModalFooter';
import ViewDefinitionEditor from './ViewDefinitionEditor';
import Confirm from 'components/confirm/Confirm';
import Modal from 'components/modal/Modal';

/**
 * ViewModal
 *
 * Modal for adding a new view.
 *
 * @returns {ReactNode} The rendered component.
 */
const ViewModal = ({
  isOpen,
  setOpen,
  typeKey,
  schemaFields = null,
  baseKey,
  createdFrom,
  dashboardKey,
  nodeKey,
  parentTypeKey,
  view = null,
  viewGroups = null,
  mode = 'create',
  onSave,
  onSaveDefinition,
  onDeleteDefinition,
  defaultTitlePrefix = '',
  createPosition = null,
  initialViewType = 'LIST',
  contextHint = '',
}) => {

  // ----- CONTEXT ----- //

  const { showProgress, hideProgress } = useProgress();
  const { theme } = useTheme();
  const { user } = useUser();

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  const isEditMode = mode === 'edit';

  // ----- STATE ----- //

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  // ----- HOOKS ----- //

  const {
    editorProps,
    filterFieldMap,
    isGalleryDataType,
    modalState,
    scheduledFilterFieldMap,
    scheduledTemporalFieldMap,
  } = useViewDefinitionEditor({
    defaultTitlePrefix,
    initialViewType,
    isEditMode,
    isOpen,
    nodeKey,
    parentTypeKey,
    schemaFields,
    typeKey,
    view,
    viewGroups,
  });

  // ----- HANDLERS ----- //

  /**
 * Handles save.
 *
 * @param {*} useViewModalSave
 */

  const handleSave = useViewModalSave({
    boardEnumOptions: modalState.boardEnumOptions,
    boardField: modalState.boardField,
    boardLayout: modalState.boardLayout,
    viewGroupId: modalState.viewGroupId,
    calendarField: modalState.calendarField,
    chartConfig: modalState.chartConfig,
    createPosition,
    createdFrom,
    dashboardKey,
    description: modalState.description,
    filterFieldMap,
    filters: modalState.filters,
    hideProgress,
    includeDescendants: modalState.includeDescendants,
    isEditMode,
    isGalleryDataType,
    nodeKey,
    onSave,
    properties: modalState.properties,
    scheduledField: modalState.scheduledField,
    scheduledFilterFieldMap,
    scheduledFilters: modalState.scheduledFilters,
    scheduledProperties: modalState.scheduledProperties,
    scheduledTemporalFieldMap,
    scheduledType: modalState.scheduledType,
    setOpen,
    showProgress,
    showUnscheduled: modalState.showUnscheduled,
    preset: modalState.preset,
    showHierarchy: modalState.showHierarchy,
    showDependencies: modalState.showDependencies,
    allowScheduleEdit: modalState.allowScheduleEdit,
    slotIncrementMinutes: modalState.slotIncrementMinutes,
    sorts: modalState.sorts,
    timelineField: modalState.timelineField,
    timelineGroupField: modalState.timelineGroupField,
    timelineRowMode: modalState.timelineRowMode,
    title: modalState.title,
    type: modalState.type,
    typeKey,
    user,
    view,
    baseKey,
  });

  /**
 * Handles save definition.
 */
  const handleSaveDefinition = useCallback(async () => {
    if (!onSaveDefinition) return;

    const nextView = buildViewDefinitionDraft({
      description: modalState.description,
      title: modalState.title,
      type: modalState.type,
      boardField: modalState.boardField,
      boardLayout: modalState.boardLayout,
      boardEnumOptions: modalState.boardEnumOptions,
      viewGroupId: modalState.viewGroupId,
      properties: modalState.properties,
      filters: modalState.filters,
      sorts: modalState.sorts,
      includeDescendants: modalState.includeDescendants,
      timelineField: modalState.timelineField,
      timelineRowMode: modalState.timelineRowMode,
      timelineGroupField: modalState.timelineGroupField,
      showUnscheduled: modalState.showUnscheduled,
      preset: modalState.preset,
      showHierarchy: modalState.showHierarchy,
      showDependencies: modalState.showDependencies,
      allowScheduleEdit: modalState.allowScheduleEdit,
      calendarField: modalState.calendarField,
      chartConfig: modalState.chartConfig,
      scheduledType: modalState.scheduledType,
      scheduledField: modalState.scheduledField,
      slotIncrementMinutes: modalState.slotIncrementMinutes,
      scheduledFilters: modalState.scheduledFilters,
      scheduledProperties: modalState.scheduledProperties,
      isGalleryDataType,
      filterFieldMap,
      scheduledFilterFieldMap,
      scheduledTemporalFieldMap,
    });
    if (!nextView) return;

    try {
      await onSaveDefinition(nextView);
      setOpen(false);
    } catch (error) {
      console.error('[ViewModal] save definition error:', error);
    }
  }, [
    filterFieldMap,
    isGalleryDataType,
    modalState,
    onSaveDefinition,
    scheduledFilterFieldMap,
    scheduledTemporalFieldMap,
    setOpen,
  ]);

  /**
 * Handles delete.
 */
  const handleDelete = useCallback(async () => {
    if (onDeleteDefinition) {
      try {
        await onDeleteDefinition(view || null);
        setConfirmDeleteOpen(false);
        setOpen(false);
      } catch (error) {
        console.error('[ViewModal] delete definition error:', error);
      }
      return;
    }

    if (!view?.key) return;

    try {
      showProgress && showProgress('Deleting view...');
      await deleteViewByKey(view.key);
      setConfirmDeleteOpen(false);
      setOpen(false);
    } catch (error) {
      console.error('[ViewModal] delete view error:', error);
    } finally {
      hideProgress && hideProgress();
    }
  }, [view, showProgress, hideProgress, onDeleteDefinition, setOpen]);

  const canDeleteView = isEditMode;

  // ----- RENDER ----- //

  return (
    <>
      <Modal
        iconKey="view_compact_alt"
        title={isEditMode ? 'Edit View' : 'Add View'}
        isOpen={isOpen}
        width={isMobile ? '100%' : 560}
        height="auto"
        onClose={() => setOpen(false)}
      >
        {contextHint ? (
          <div
            className="view-modal-context-hint"
            style={{
              color: theme.foregroundMuted,
              fontSize: 13,
              lineHeight: 1.5,
              marginBottom: 12,
            }}
          >
            {contextHint}
          </div>
        ) : null}

        <ViewDefinitionEditor {...editorProps} />

        <ViewModalFooter
          isEditMode={isEditMode}
          canDelete={canDeleteView}
          onCancel={() => setOpen(false)}
          onDelete={() => {
            if (!canDeleteView) return;
            setConfirmDeleteOpen(true);
          }}
          onSave={onSaveDefinition ? handleSaveDefinition : handleSave}
        />
      </Modal>

      <Confirm
        title="Delete View"
        message="Are you sure you want to delete this view? This cannot be undone."
        isOpen={confirmDeleteOpen}
        setOpen={setConfirmDeleteOpen}
        onConfirm={handleDelete}
      />
    </>
  );
};

// Dev Tools
ViewModal.displayName = 'ViewModal';

export default ViewModal;
